import { formatDistanceToNowStrict } from "date-fns";
import type {
  ListSessionsResponseDTO,
  SessionDeviceDTO,
} from "../../domain/dto/SessionDTO";
import type { DeviceSession } from "../../domain/entities/DeviceSession";
import { parseUserAgentDeviceName } from "../../domain/utils/deviceName";

const resolveDeviceName = (dto: SessionDeviceDTO): string => {
  if (dto.device_name?.trim()) return dto.device_name.trim();
  if (dto.device?.trim()) return dto.device.trim();
  const browser = dto.browser?.trim();
  const os = (dto.os ?? dto.platform)?.trim();
  if (browser && os) return `${browser} on ${os}`;
  if (browser || os) return browser || os || "Unknown device";
  return parseUserAgentDeviceName(dto.user_agent).deviceName;
};

const resolveDeviceKind = (dto: SessionDeviceDTO): DeviceSession["deviceKind"] => {
  if (dto.device_type === "mobile") return "mobile";
  if (dto.device_type === "desktop") return "desktop";
  const haystack = `${dto.device_type ?? ""} ${dto.device ?? ""} ${dto.os ?? ""} ${dto.platform ?? ""} ${dto.user_agent ?? ""}`.toLowerCase();
  return /mobile|iphone|android|ios|ipad|dart:io/.test(haystack) ? "mobile" : "desktop";
};

const resolveLocation = (dto: SessionDeviceDTO): string => {
  if (dto.location?.trim()) return dto.location.trim();
  const parts = [dto.city, dto.country].filter((part) => part?.trim());
  if (parts.length) return parts.join(", ");
  return dto.ip_address?.trim() || "Unknown location";
};

const resolveLastActiveLabel = (dto: SessionDeviceDTO, isCurrent: boolean): string => {
  if (isCurrent) return "Active now";
  const raw = dto.last_active_at ?? dto.last_active ?? dto.last_seen_at ?? dto.created_at;
  if (!raw) return "Last active unknown";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "Last active unknown";
  return `Last active ${formatDistanceToNowStrict(date, { addSuffix: true })}`;
};

export const mapSessionDTO = (dto: SessionDeviceDTO): DeviceSession => {
  const isCurrent = Boolean(dto.is_current ?? dto.current);
  return {
    id: dto.id ?? dto.session_id ?? "",
    deviceName: resolveDeviceName(dto),
    deviceKind: resolveDeviceKind(dto),
    location: resolveLocation(dto),
    isCurrent,
    lastActiveLabel: resolveLastActiveLabel(dto, isCurrent),
  };
};

export const mapSessionsResponse = (
  response: ListSessionsResponseDTO,
): DeviceSession[] => {
  const { data } = response;
  const sessions = Array.isArray(data)
    ? data
    : typeof data === "object" && data
      ? (data.sessions ?? [])
      : [];
  return sessions.map(mapSessionDTO);
};
